import React, { FC, ReactNode, useState } from "react"
import { Button, Icon, PageTitle, Spinner, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router-dom"
import linerMaterialStore from "./stores/liner-material-store"
import LinerMaterialLibraryModal from "./components/liner-material-library-modal"
import LinerMaterialDeleteModal from "./components/liner-material-delete-modal"
import { NavigationCloseEntityOptions, NavigationDeleteEntityOptions } from "components/utils/modal-helper"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import ListView from "components/utils/list-view/list-view"
import { LinerSpecification } from "./models/liner-material-library-models"

const LinerMaterialLibraryListView: FC = observer(() => {
    const navigate = useNavigate()
    const { formatMessage } = useIntl()
    const { linerMaterials } = linerMaterialStore
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

    const [linerSpecificationToEdit, setLinerSpecificationToEdit] = useState<LinerSpecification | undefined>(undefined)
    const [isLinerSpecificationEditModalOpen, setIsLinerSpecificationEditModalOpen] = useState(false)

    const [linerSpecificationToDelete, setLinerSpecificationToDelete] = useState<LinerSpecification | undefined>(undefined)
    const [isLinerSpecificationDeleteModalOpen, setIsLinerSpecificationDeleteModalOpen] = useState(false)
    const { setMessage } = useToast()

    const createLinerMaterialModal = {
        open: () => {
            setIsCreateModalOpen(true)
        },
        close: (navigationCloseEntityOptions?: NavigationCloseEntityOptions) => {
            setIsCreateModalOpen(false)
            if (navigationCloseEntityOptions?.passed) showNotifier(`${navigationCloseEntityOptions.entityName} has been created!`)
        },
    }

    const deleteLinerMaterialModal = {
        open: (event: React.MouseEvent, linerSpecification: LinerSpecification) => {
            setLinerSpecificationToDelete(linerSpecification)
            setIsLinerSpecificationDeleteModalOpen(true)
        },
        close: (params: NavigationDeleteEntityOptions) => {
            setIsLinerSpecificationDeleteModalOpen(false)
            setLinerSpecificationToDelete(undefined)
            if (params?.deleted) showNotifier(`${params.entityName} has been deleted!`)
        },
    }

    const editLinerMaterialModal = {
        open: (event: React.MouseEvent, linerSpecification: LinerSpecification) => {
            setLinerSpecificationToEdit(linerSpecification)
            setIsLinerSpecificationEditModalOpen(true)
        },
        close: (navigationCloseEntityOptions?: NavigationCloseEntityOptions) => {
            setIsLinerSpecificationEditModalOpen(false)
            setLinerSpecificationToEdit(undefined)
            if (navigationCloseEntityOptions?.passed) showNotifier(`${navigationCloseEntityOptions.entityName} has been edited!`)
        },
    }

    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }

    const template = [
        {
            headingKey: "linerMaterialLibrary.headings.materialReference",
            cellFunc: (s: LinerSpecification) => s.materialReference,
            isSearchable: true,
        },
        {
            headingKey: "linerMaterialLibrary.headings.brand",
            cellFunc: (s: LinerSpecification) => s.brand,
            isSearchable: true,
        },
        {
            headingKey: "linerMaterialLibrary.headings.grade",
            cellFunc: (s: LinerSpecification) => s.grade,
            isSearchable: true,
        },
        {
            headingKey: "linerMaterialLibrary.headings.materialType",
            cellFunc: (s: LinerSpecification) => s.materialType,
            isSearchable: true,
        },
        {
            headingKey: "linerMaterialLibrary.headings.thickness",
            cellFunc: (s: LinerSpecification) => s.thickness,
            isSearchable: true,
        },
        {
            headingKey: "linerMaterialLibrary.headings.wearSurfaceThickness",
            cellFunc: (s: LinerSpecification) => s.wearSurfaceThickness,
            isSearchable: true,
        },
        {
            headingKey: "linerMaterialLibrary.headings.backingThickness",
            cellFunc: (s: LinerSpecification) => s.backingThickness,
            isSearchable: true,
        },
        {
            headingKey: "headings.delete",
            cellFunc: (s: LinerSpecification) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon name='icon-trash-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => deleteLinerMaterialModal.open(event, s)} />
                </span>
            ),
        },
        {
            headingKey: "headings.edit",
            cellFunc: (s: LinerSpecification) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon name='icon-edit-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => editLinerMaterialModal.open(event, s)} />
                </span>
            ),
        },
    ]

    const rightPageButtons: ReactNode = (
        <>
            <Button icon='icon-plus-16' labelPosition='left' onClick={createLinerMaterialModal.open} primary>
                {formatMessage("linerMaterial.create")}
            </Button>
        </>
    )
    const tableContainerStyles = {
        ...itemViewDetailsContainerStyles,
        height: "91%",
    }

    const breadcrumbsPath: { text: string; to: string }[] = [
        {
            text: formatMessage("breadcrumb.home"),
            to: "/",
        },
        {
            text: formatMessage("linerMaterialLibrary.breadcrumb"),
            to: "/liner-material-library",
        },
    ]

    return (
        <>
            <PageTitle
                breadcrumbsPath={breadcrumbsPath}
                onPathChange={navigate}
                title={formatMessage("linerMaterialLibrary.title")}
                extraItems={rightPageButtons}
            />
            <div style={tableContainerStyles}>
                {linerMaterials.length < 1 ? (
                    <Spinner text={formatMessage("linerMaterial.loading")} />
                ) : (
                    <ListView noneYetKey={"linerMaterial.nonYet"} template={template} data={linerMaterials} />
                )}
                {isCreateModalOpen && <LinerMaterialLibraryModal isOpen={isCreateModalOpen} onClose={createLinerMaterialModal.close} />}
                {isLinerSpecificationEditModalOpen && (
                    <LinerMaterialLibraryModal
                        isOpen={isLinerSpecificationEditModalOpen}
                        onClose={editLinerMaterialModal.close}
                        linerMaterial={linerSpecificationToEdit}
                    />
                )}
                {isLinerSpecificationDeleteModalOpen && (
                    <LinerMaterialDeleteModal
                        isOpen={isLinerSpecificationDeleteModalOpen}
                        onClose={deleteLinerMaterialModal.close}
                        entity={linerSpecificationToDelete}
                    />
                )}
            </div>
        </>
    )
})

export default LinerMaterialLibraryListView
